import React, { useEffect, useCallback, useState } from "react";
import {
  Button,
  ButtonSize,
  Image,
  RadioButton,
  RadioButtonGroup,
  Title,
} from "@bale/ui";
import Footer from "../../commons/footer/Footer";
import { strings } from "intl/fa";
import styles from "./SelectAdSpot.module.scss";
import { useAppDispatch, useAppSelector } from "utils/store";
import { OrderAdActions, OrderAdPages } from "core/orderAd/orderAd.slice";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import { useTheme } from "@emotion/react";
import { AdSpot } from "core/orderAd/orderAd.entity";
import { AccountType } from "../../../core/auth/auth.entity";
import { useSponsoredMessages } from "hooks/useFeatureScopes";

export const AdSpotText = {
  [AdSpot.AdSpotDialog]:
    strings.dialog_ad_form.select_ad_type.spot_texts.dialog,
  [AdSpot.AdSpotOnBoarding]:
    strings.dialog_ad_form.select_ad_type.spot_texts.onboarding,
  [AdSpot.AdSpotMyBank]:
    strings.dialog_ad_form.select_ad_type.spot_texts.mybank,
  [AdSpot.AdSpotSponsoredMessage]:
    strings.dialog_ad_form.select_ad_type.spot_texts.sponsored_message,
};

const getPreview = (placement: AdSpot) => {
  const base = "/images/";
  switch (+placement) {
    case AdSpot.AdSpotDialog:
      return base + "dialogPreview.webp";
    case AdSpot.AdSpotMyBank:
      return base + "myBankPreview.webp";
    case AdSpot.AdSpotOnBoarding:
      return base + "onboardingPreview.webp";
    case AdSpot.AdSpotSponsoredMessage:
      return base + "sponsored_preview.webp";
    default:
      return base + "dialogPreview.webp";
  }
};

type Props = {
  isCampaign?: boolean;
};
const SelectAdType: React.FC<React.PropsWithChildren<Props>> = ({ isCampaign }) => {
  const dispatch = useAppDispatch();
  const myPanel = useAppSelector((state) => state.auth.myPanel);
  const [selectedSpot, setSelectedSpot] = useState<AdSpot>();
  const theme = useTheme();
  const isOrganization = myPanel.type === AccountType.Organization;
  const hasSponsoredMessage = useSponsoredMessages();

  const handleSubmit = useCallback(() => {
    if (selectedSpot === AdSpot.AdSpotDialog) {
      if (isCampaign) {
        dispatch(OrderAdActions.setPage(OrderAdPages.CampaignMainInfo));
      } else {
        dispatch(OrderAdActions.setPage(OrderAdPages.DialogMainInfo));
      }
      return;
    }
    if (selectedSpot === AdSpot.AdSpotMyBank) {
      dispatch(OrderAdActions.setPage(OrderAdPages.SubmitMyBankAd));
      return;
    }
    if (selectedSpot === AdSpot.AdSpotOnBoarding) {
      dispatch(OrderAdActions.setPage(OrderAdPages.SubmitOnboardingAd));
      return;
    }
    if (selectedSpot === AdSpot.AdSpotSponsoredMessage) {
      dispatch(OrderAdActions.resetState());
      dispatch(
        OrderAdActions.setOrderAdInfo({
          spot: AdSpot.AdSpotSponsoredMessage,
        })
      );
      if (isCampaign) {
        dispatch(
          OrderAdActions.setPage(OrderAdPages.CampaignSponsoredMessageMainInfo)
        );
      } else {
        dispatch(OrderAdActions.setPage(OrderAdPages.SponsoredMessageMainInfo));
      }
      return;
    }
  }, [selectedSpot, isCampaign]);

  const optionChangeHandler = (newSpot: AdSpot) => {
    setSelectedSpot(newSpot);
  };

  useEffect(() => {
    dispatch(OrderAdActions.resetState());
  }, []);

  return (
    <>
      <div className={styles.Content}>
        <CardWrapper className={styles.Card} direction="rtl">
          <RadioButtonGroup
            title={
              <Title
                title={strings.dialog_ad_form.select_ad_type.placement_type}
                className={styles.titleWrapper}
                titleClassName={styles.Title}
              ></Title>
            }
            divider={false}
          >
            <RadioButton
              key={"dialog"}
              checked={selectedSpot === AdSpot.AdSpotDialog}
              onChange={() => optionChangeHandler(4)}
              text={AdSpotText[AdSpot.AdSpotDialog]}
              className={styles.radioButton}
            />
            {hasSponsoredMessage && (
              <RadioButton
                key={"sponsoredmessage"}
                checked={selectedSpot === AdSpot.AdSpotSponsoredMessage}
                onChange={() => optionChangeHandler(9)}
                text={AdSpotText[AdSpot.AdSpotSponsoredMessage]}
                className={styles.radioButton}
              />
            )}
          </RadioButtonGroup>
          {selectedSpot && (
            <img
              src={getPreview(selectedSpot)}
              alt=""
              className={styles.PreviewImage}
            />
          )}
        </CardWrapper>
      </div>
      <Footer>
        <Button
          text={strings.choose_ad_type.confirm}
          data-testid="confirm-button"
          size={ButtonSize.XLarge}
          onClick={handleSubmit}
          disable={!selectedSpot}
        />
      </Footer>
    </>
  );
};

export default SelectAdType;
